// Loads the InvokeAI-generated per-race unit + building sprites as Pixi textures.
// Missing files fail soft (accessor returns null) so the renderer falls back to vector icons.

import { Assets, type Texture } from 'pixi.js';

const RACES = ['orc', 'elf', 'dwarf', 'human', 'undead'] as const;
const UNIT_KIND = ['warrior', 'archer', 'rider', 'spearman'] as const; // index === UnitType
const BUILDINGS = ['market', 'barracks', 'wall'] as const;

export interface SpriteStore {
  /** The unit sprite. `skinned` uses the race's War Regalia skin (skins/<race>_<unit>.png) when it exists,
   *  falling back to the base sprite; used only for the player's own units when they own that race's set. */
  unit(race: string, type: number, skinned?: boolean): Texture | null;
  building(name: string): Texture | null;
}

/** @param equippedSkin the profile's equipped skin id (e.g. 'set_orc'); that race's War Regalia files join
 *  the first-paint set, every other race's skins load in the background. Only the player's own units can
 *  render skinned, so deferring the other 16 files (~1.4 MB) never affects what the first paint shows. */
export async function loadSprites(equippedSkin?: string): Promise<SpriteStore> {
  const map = new Map<string, Texture>();
  const load = (key: string, url: string): Promise<void> =>
    Assets.load<Texture>(url)
      .then((t) => void map.set(key, t))
      .catch(() => void 0); // missing sprite → stay unset → icon (or base-sprite) fallback

  const skinnedRace = equippedSkin?.startsWith('set_') ? equippedSkin.slice(4) : null;
  const tasks: Promise<void>[] = []; // gates the vector-to-artwork swap: keep it to what the board needs
  const deferred: Array<[string, string]> = []; // fetches must not even START until the board's set landed
  for (const r of RACES)
    for (const k of UNIT_KIND) {
      tasks.push(load(`${r}_${k}`, `sprites/${r}_${k}.png`));
      // War Regalia skins (fail soft if absent); unequipped races' skins trickle in behind the first paint
      if (r === skinnedRace) tasks.push(load(`skin_${r}_${k}`, `skins/${r}_${k}.png`));
      else deferred.push([`skin_${r}_${k}`, `skins/${r}_${k}.png`]);
    }
  for (const b of BUILDINGS) tasks.push(load(`building_${b}`, `sprites/building_${b}.png`));
  await Promise.all(tasks);
  // fills the same map after the swap; a later equip finds its textures on the next render
  void Promise.all(deferred.map(([k, u]) => load(k, u)));

  return {
    unit: (race, type, skinned) => {
      const k = UNIT_KIND[type] ?? 'warrior';
      if (skinned) return map.get(`skin_${race}_${k}`) ?? map.get(`${race}_${k}`) ?? null;
      return map.get(`${race}_${k}`) ?? null;
    },
    building: (name) => map.get(`building_${name}`) ?? null,
  };
}
